import { ORPCError } from "@orpc/client";
import { adminUserSchema } from "@rosc/contracts";
import { Badge } from "@rosc/ui/components/badge";
import { Button } from "@rosc/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@rosc/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@rosc/ui/components/dialog";
import { Input } from "@rosc/ui/components/input";
import { Skeleton } from "@rosc/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rosc/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { useState } from "react";
import { Line } from "react-chartjs-2";
import { adminKeys } from "@/lib/admin-queries";
import { beginImpersonation } from "@/lib/impersonation";
import { referenceClient } from "@/lib/orpc/client";
import { useAuthStore } from "@/store/auth.store";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);
const minutes = (seconds: number) =>
  (seconds / 60).toLocaleString(undefined, { maximumFractionDigits: 1 });
const actionLabels = {
  admin: ["Grant admin", "Confirm grant"],
  user: ["Revoke admin", "Confirm revoke"],
  ban: ["Ban account", "Confirm ban"],
  unban: ["Unban account", "Confirm unban"],
  remove: ["Remove account", "Confirm removal"],
  impersonate: ["View as user", "Start read-only session"],
  password: ["Set password", "Confirm password"],
};

export function AdminUserDetail({ publicId }: { publicId: string }) {
  const uid = useAuthStore((state) => state.user?.uid);
  const queryClient = useQueryClient();
  const [action, setAction] = useState<{
    publicId: string;
    kind: keyof typeof actionLabels;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: async (action: {
      publicId: string;
      kind: keyof typeof actionLabels;
      reason: string;
    }) => {
      const options = { context: { expectedUserUid: uid } };
      if (action.kind === "password")
        return referenceClient.adminSetPassword(
          {
            params: { publicId: action.publicId },
            body: { reason: action.reason, password },
          },
          options,
        );
      if (action.kind === "impersonate") {
        await referenceClient.adminImpersonate(
          {
            params: { publicId: action.publicId },
            body: { reason: action.reason },
          },
          options,
        );
        beginImpersonation();
        return;
      }
      if (
        action.kind === "ban" ||
        action.kind === "unban" ||
        action.kind === "remove"
      ) {
        const change =
          action.kind === "ban"
            ? referenceClient.adminBan
            : action.kind === "unban"
              ? referenceClient.adminUnban
              : referenceClient.adminRemove;
        return change(
          {
            params: { publicId: action.publicId },
            body: { reason: action.reason },
          },
          options,
        );
      }
      const change =
        action.kind === "admin"
          ? referenceClient.adminGrantRole
          : referenceClient.adminRevokeRole;
      return change(
        {
          params: { publicId: action.publicId },
          body: { role: action.kind, reason: action.reason },
        },
        options,
      );
    },
    onSuccess: async () => {
      setAction(null);
      setPassword("");
      await queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
    onSettled: () => setPassword(""),
    retry: false,
  });
  const query = useQuery({
    queryKey: adminKeys(uid ?? "").user(publicId),
    queryFn: async ({ signal }) =>
      adminUserSchema.parse(
        await referenceClient.adminUser(
          { params: { publicId } },
          { signal, context: { expectedUserUid: uid } },
        ),
      ),
    enabled: !!uid,
    retry: false,
  });
  if (query.isPending)
    return <Skeleton className="h-64 w-full" aria-label="Loading admin user" />;
  if (query.isError) {
    if (query.error instanceof ORPCError && query.error.code === "NOT_FOUND")
      return <p role="status">User not found.</p>;
    return (
      <div role="alert">
        Unable to load admin user.{" "}
        <Button onClick={() => void query.refetch()}>Try again</Button>
      </div>
    );
  }
  const { account, usage, dailyMinutes, aiByTool, lastRecordingAt } =
    query.data;
  return (
    <section className="space-y-6" aria-labelledby="admin-user-title">
      <h2 id="admin-user-title" className="text-xl font-semibold break-all">
        {account.email}
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            {[
              ["Signup date", account.createdAt ?? "Unknown"],
              ["Plan", account.planName ?? "Unknown"],
              ["Subscription stored status", account.planStatus ?? "Unknown"],
              [
                "Two-factor authentication",
                account.hasLogin
                  ? account.twoFactorEnabled
                    ? "Enabled"
                    : "Disabled"
                  : "No login",
              ],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap gap-2">
            {account.isAdmin && <Badge>Admin</Badge>}
            {account.banned && <Badge variant="destructive">Banned</Badge>}
            {account.deletionRequested && (
              <Badge variant="outline">Deletion requested</Badge>
            )}
            {!account.hasLogin && (
              <Badge variant="secondary">Not on the new login yet</Badge>
            )}
          </div>
          {account.banned && account.banReason && <p>{account.banReason}</p>}
          <p className="text-sm text-muted-foreground">
            Subscription stored status may briefly lag Stripe.
          </p>
          {account.hasLogin && (
            <Button
              variant="outline"
              onClick={() => {
                mutation.reset();
                setReason("");
                setAction({
                  publicId,
                  kind: account.isAdmin ? "user" : "admin",
                });
              }}
            >
              {account.isAdmin ? "Revoke admin" : "Grant admin"}
            </Button>
          )}
          {account.hasLogin && !account.isAdmin && (
            <Button
              variant={account.banned ? "outline" : "destructive"}
              onClick={() => {
                mutation.reset();
                setReason("");
                setAction({
                  publicId,
                  kind: account.banned ? "unban" : "ban",
                });
              }}
            >
              {account.banned ? "Unban account" : "Ban account"}
            </Button>
          )}
          {account.hasLogin &&
            !account.isAdmin &&
            !account.banned &&
            !account.deletionRequested && (
              <Button
                variant="outline"
                onClick={() => {
                  mutation.reset();
                  setReason("");
                  setAction({ publicId, kind: "impersonate" });
                }}
              >
                View as user
              </Button>
            )}
          {account.hasLogin && !account.isAdmin && (
            <Button
              variant="destructive"
              onClick={() => {
                mutation.reset();
                setReason("");
                setAction({ publicId, kind: "remove" });
              }}
            >
              Remove account
            </Button>
          )}
          {account.hasLogin &&
            account.emailVerified &&
            account.loginEmail?.toLowerCase().endsWith("@rosc.ai") &&
            !account.isAdmin && (
              <Button
                variant="outline"
                onClick={() => {
                  mutation.reset();
                  setReason("");
                  setPassword("");
                  setAction({ publicId, kind: "password" });
                }}
              >
                Set password
              </Button>
            )}
        </CardContent>
      </Card>
      <Dialog
        open={action?.publicId === publicId}
        onOpenChange={(open) => {
          if (!open && !mutation.isPending) {
            setAction(null);
            setPassword("");
          }
        }}
      >
        <DialogContent showCloseButton={!mutation.isPending}>
          <DialogHeader>
            <DialogTitle>{action && actionLabels[action.kind][0]}</DialogTitle>
            <DialogDescription>
              {action?.kind === "password"
                ? `Set a new password for ${account.email} and sign out all their sessions.`
                : action?.kind === "impersonate"
                  ? `View ${account.email} in a read-only session for up to one hour.`
                  : action?.kind === "remove"
                    ? `Remove ${account.email} through the account deletion flow. Access is denied immediately; data is purged later.`
                    : action?.kind === "ban"
                      ? `Ban ${account.email} and sign out all their sessions.`
                      : action?.kind === "unban"
                        ? `Restore sign-in for ${account.email}. Old sessions stay signed out.`
                        : `Change admin access for ${account.email}.`}{" "}
              A reason is required and will be saved in the audit log.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (
                action?.publicId === publicId &&
                reason.trim().length >= 3 &&
                (action.kind !== "password" || password.length > 0) &&
                !mutation.isPending
              )
                mutation.mutate({ ...action, reason: reason.trim() });
            }}
            className="space-y-4"
          >
            {action?.kind === "password" && (
              <>
                <label htmlFor="admin-action-password">New password</label>
                <Input
                  id="admin-action-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  disabled={mutation.isPending}
                />
              </>
            )}
            <label htmlFor="admin-action-reason">Reason</label>
            <Input
              id="admin-action-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={3}
              maxLength={1000}
              required
              disabled={mutation.isPending}
            />
            {mutation.isError && (
              <p role="alert">
                {mutation.error instanceof ORPCError &&
                mutation.error.code === "ADMIN_FACTOR_REQUIRED"
                  ? "Add a verified authenticator app or passkey before granting admin."
                  : mutation.error instanceof ORPCError &&
                      mutation.error.code === "TARGET_IS_ADMIN"
                    ? action?.kind === "remove"
                      ? "Revoke admin access before removing this account."
                      : "Revoke admin access before changing this account's ban."
                    : mutation.error instanceof ORPCError &&
                        mutation.error.code === "FORBIDDEN"
                      ? "Not authorized"
                      : mutation.error instanceof ORPCError &&
                          mutation.error.code === "LAST_ADMIN"
                        ? "The last admin cannot be revoked."
                        : mutation.error instanceof ORPCError &&
                            mutation.error.code === "ADMIN_TARGET_NO_LOGIN"
                          ? "This account has no login yet."
                          : mutation.error instanceof ORPCError &&
                              mutation.error.code === "ADMIN_STEP_UP_REQUIRED"
                            ? "Confirm your identity, then try again."
                            : action?.kind === "password"
                              ? "Unable to set the password. Check the password requirements and try again."
                              : action?.kind === "remove"
                                ? "Unable to remove the account. Try again."
                                : action?.kind === "ban" ||
                                    action?.kind === "unban"
                                  ? "Unable to change the account ban. Try again."
                                  : "Unable to change admin access. Try again."}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => {
                  setAction(null);
                  setPassword("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  reason.trim().length < 3 ||
                  (action?.kind === "password" && password.length === 0) ||
                  mutation.isPending
                }
              >
                {mutation.isPending
                  ? "Saving..."
                  : action && actionLabels[action.kind][1]}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Card>
        <CardHeader>
          <CardTitle>Recording usage this period</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p>
            {minutes(usage.subscription.usedSeconds)} /{" "}
            {minutes(usage.subscription.allowanceSeconds)} minutes
          </p>
          <p>
            {minutes(usage.subscription.availableSeconds)} minutes remaining
          </p>
          <p>{minutes(usage.additional.availableSeconds)} additional minutes</p>
          <p className="text-sm text-muted-foreground">
            Period: {usage.subscription.periodStart ?? "Unknown"} to{" "}
            {usage.subscription.periodEnd ?? "Unknown"}
          </p>
          <p>
            Last recording:{" "}
            {lastRecordingAt ? (
              <time dateTime={lastRecordingAt}>{lastRecordingAt}</time>
            ) : (
              <span>No recordings yet.</span>
            )}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Daily recording minutes</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-muted-foreground">
            Last 30 days in America/Toronto.
          </p>
          <div className="h-72">
            <Line
              role="img"
              aria-label="Daily recording minutes over the last 30 days"
              data={{
                labels: dailyMinutes.map((day) => day.date),
                datasets: [
                  {
                    label: "Recording minutes",
                    data: dailyMinutes.map((day) => day.minutes),
                    borderColor: "#0f766e",
                    backgroundColor: "#0f766e",
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: "Minutes" },
                  },
                },
              }}
            />
          </div>
        </CardContent>
      </Card>
      {aiByTool.length === 0 ? (
        <p role="status">No AI activity.</p>
      ) : (
        <Table>
          <TableCaption>AI usage by tool over the last 30 days.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Tool</TableHead>
              <TableHead>Actions</TableHead>
              <TableHead>Input tokens</TableHead>
              <TableHead>Output tokens</TableHead>
              <TableHead>Cost USD</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aiByTool.map((tool) => (
              <TableRow key={tool.tool}>
                <TableCell>{tool.tool}</TableCell>
                <TableCell>{tool.actions.toLocaleString()}</TableCell>
                <TableCell>{tool.inputTokens.toLocaleString()}</TableCell>
                <TableCell>{tool.outputTokens.toLocaleString()}</TableCell>
                <TableCell>
                  {tool.costUsd.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
